import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { writeSelectedUserGroupIdCookie } from '@/features/business/utils/selectedUserGroupCookie'
import { selectedUserGroupQueryOptions } from '@/features/account/services/Business/queryOptions'
import { createPassiveQueryStore } from '@/features/shared/utils/dataFetching/createPassiveQueryStore'

const useSelectedUserGroupId = createPassiveQueryStore(() => selectedUserGroupQueryOptions)

/**
 * Provides the currently selected user group id (the value of the
 * `s_group` session cookie) to both client and server React trees. On the
 * server the value is seeded into react query cache from `cookies` (passed in from
 * `getServerSideProps`/`req.cookies` or from an app-router server
 * component reading `next/headers`). On the client the same cookie is
 * read via `js-cookie` so SSR and CSR agree on the initial value and we
 * avoid hydration mismatches.
 *
 * Note: this is the *selected* group id only. To read the list of groups
 * a user belongs to, use `usePersona` from the business hooks.
 */
export const useSelectedUserGroup = () => {
  const queryClient = useQueryClient()
  const selectedUserGroupId = useSelectedUserGroupId()

  const setSelectedUserGroupId = useCallback(
    (value: string) => {
      const previous = queryClient.getQueryData(selectedUserGroupQueryOptions.queryKey)
      writeSelectedUserGroupIdCookie(value)
      queryClient.setQueryData(selectedUserGroupQueryOptions.queryKey, value)

      // The selected group id is sent as the `x-group-id-ext` header on API
      // requests, so backend services scope response data by persona. When
      // the persona changes, any cached data from the previous persona is
      // stale. As an MVP we invalidate every query; a follow-up can scope
      // this more narrowly to only persona-partitioned queries.
      if (previous !== value) {
        queryClient.invalidateQueries()
      }
    },
    [queryClient],
  )

  return { selectedUserGroupId, setSelectedUserGroupId }
}
